/**
 * Layout of the floating badges over units and buildings (`BadgeBatch`), in
 * CSS pixels around the badge's anchor (centred on it, y up), mirroring the
 * chips they replace: rounded pills with a border, glyphs on them, laid out in
 * a row with a small gap. Pure, so it's tested without a canvas; `measure`
 * gives a glyph's advance at a font size.
 */

/** A glyph's advance width in CSS px at `font` px. */
export type Measure = (glyph: string, font: number, bold: boolean) => number;

export type BadgeQuad =
  | {
      kind: 'pill';
      /** Centre and size in px; fully rounded (a circle when square). */
      x: number;
      y: number;
      w: number;
      h: number;
      /** Draws the 1px `line` border. */
      border: boolean;
      /** Bobs up and down (the activity icon). */
      bob: boolean;
    }
  | {
      kind: 'glyph';
      /** Centre of the glyph's advance box and its line height in px. */
      x: number;
      y: number;
      w: number;
      h: number;
      glyph: string;
      font: number;
      bold: boolean;
      /** Multiplies the glyph (white text takes it; colour emoji are drawn as they are with white). */
      tint: string;
      bob: boolean;
    };

/** Theme colours (`index.css`): badge text, rank chevrons, heal cross. */
export const BADGE_COLORS = {
  fg: '#ffffff',
  warn: '#fcd34d',
  ok: '#6ee7b7',
} as const;

/** Gap between a unit's chips (`gap-0.5`). */
const GAP = 2;
/** The small chips: `h-4.5`, `px-1`, a 1px border. */
const CHIP_H = 18;
const CHIP_PAD = 4 + 1;
/** The activity circle: `h-6 w-6`, `text-xs`. */
const ACTIVITY = 24;
const ACTIVITY_FONT = 12;
/** A glyph's line box height for its font size (`leading-none` + room for emoji). */
const LINE = 1.25;

/** What a unit's badge row shows; null/0 leaves a chip out. */
export interface UnitBadgeInfo {
  spyMarker: string | null;
  rank: number;
  activityIcon: string | null;
  /** The activity icon is the heal cross: bold, in `ok`. */
  heal: boolean;
  carryIcon: string | null;
  carryAmount: number;
  /** A worker held on its job (`assign`): a 📌 chip. */
  pinned: boolean;
}

interface Piece {
  /** Treated as one glyph (an emoji), or split into characters (text). */
  text: string;
  split: boolean;
  font: number;
  bold: boolean;
  tint: string;
}

interface Chip {
  w: number;
  h: number;
  border: boolean;
  bob: boolean;
  /** Width between the pieces (`gap-px` in the load chip). */
  gap: number;
  pieces: Piece[];
}

function glyphsOf(piece: Piece): string[] {
  return piece.split ? [...piece.text] : [piece.text];
}

function piecesWidth(chip: Pick<Chip, 'gap' | 'pieces'>, measure: Measure): number {
  let w = 0;
  chip.pieces.forEach((p, i) => {
    if (i > 0) w += chip.gap;
    for (const g of glyphsOf(p)) w += measure(g, p.font, p.bold);
  });
  return w;
}

function textChip(pieces: Piece[], measure: Measure, gap = 0): Chip {
  const inner = piecesWidth({ gap, pieces }, measure);
  return { w: inner + 2 * CHIP_PAD, h: CHIP_H, border: true, bob: false, gap, pieces };
}

/** Lay chips out in a centred row: pills first, then their glyphs (drawn over them). */
function layoutRow(chips: Chip[], measure: Measure): BadgeQuad[] {
  const total = chips.reduce((w, c) => w + c.w, 0) + GAP * Math.max(0, chips.length - 1);
  const quads: BadgeQuad[] = [];
  let left = -total / 2;
  for (const chip of chips) {
    const cx = left + chip.w / 2;
    quads.push({
      kind: 'pill',
      x: cx,
      y: 0,
      w: chip.w,
      h: chip.h,
      border: chip.border,
      bob: chip.bob,
    });
    let x = cx - piecesWidth(chip, measure) / 2;
    chip.pieces.forEach((p, i) => {
      if (i > 0) x += chip.gap;
      for (const g of glyphsOf(p)) {
        const w = measure(g, p.font, p.bold);
        quads.push({
          kind: 'glyph',
          x: x + w / 2,
          y: 0,
          w,
          h: p.font * LINE,
          glyph: g,
          font: p.font,
          bold: p.bold,
          tint: p.tint,
          bob: chip.bob,
        });
        x += w;
      }
    });
    left += chip.w + GAP;
  }
  return quads;
}

const emoji = (text: string, font: number, tint: string = BADGE_COLORS.fg): Piece => ({
  text,
  split: false,
  font,
  bold: false,
  tint,
});
const text = (
  value: string,
  font: number,
  tint: string = BADGE_COLORS.fg,
  bold = false
): Piece => ({
  text: value,
  split: true,
  font,
  bold,
  tint,
});

/**
 * A unit's row: spy status, rank chevrons, the (bobbing) activity icon, and
 * its load (icon and amount) and a 📌 when pinned. Empty when there's nothing to show.
 */
export function unitBadgeQuads(info: UnitBadgeInfo, measure: Measure): BadgeQuad[] {
  const chips: Chip[] = [];
  if (info.spyMarker) {
    // Every spy marker is a colour emoji, so it isn't tinted (the old chip's
    // `text-danger` on an exposed one had no effect on it either).
    chips.push(textChip([emoji(info.spyMarker, 9, BADGE_COLORS.fg)], measure));
  }
  if (info.rank > 0)
    chips.push(textChip([text('▲'.repeat(info.rank), 8, BADGE_COLORS.warn)], measure));
  if (info.activityIcon) {
    const icon = info.heal
      ? text(info.activityIcon, ACTIVITY_FONT, BADGE_COLORS.ok, true)
      : emoji(info.activityIcon, ACTIVITY_FONT);
    chips.push({ w: ACTIVITY, h: ACTIVITY, border: true, bob: true, gap: 0, pieces: [icon] });
  }
  if (info.pinned) chips.push(textChip([emoji('📌', 9)], measure));
  if (info.carryIcon) {
    chips.push(textChip([emoji(info.carryIcon, 9), text(String(info.carryAmount), 9)], measure, 1));
  }
  return chips.length > 0 ? layoutRow(chips, measure) : [];
}

/**
 * The "👷 3/5" count over a building with units inside (a borderless chip,
 * `px-1.5`, 16px high), with a 📌 when a pinned worker is among them.
 */
export function insideBadgeQuads(label: string, measure: Measure, pinned = false): BadgeQuad[] {
  const pieces = [emoji('👷', 10), text(` ${label}`, 10)];
  if (pinned) pieces.push(emoji('📌', 10));
  const w = piecesWidth({ gap: 0, pieces }, measure) + 12;
  return layoutRow([{ w, h: 16, border: false, bob: false, gap: 0, pieces }], measure);
}

/** The activity circle's bob (badgeBatch's BOB_PX), which the name must clear. */
const BOB = 2;
/** Map name labels: small bold text with a 1px shadow, over the badge row. */
const NAME_FONT = 10;
const NAME_SHADOW = '#0c0e14';
/** Anchor to the name's centre: clears the tallest chip (the bobbing activity circle) with a 2px gap. */
export const NAME_LIFT = ACTIVITY / 2 + BOB + 2 + (NAME_FONT * LINE) / 2;

// Built on first use, and only where the browser has it: else split by code point.
let graphemes: Intl.Segmenter | null | undefined;
function splitGraphemes(text: string): string[] {
  if (graphemes === undefined) {
    graphemes =
      typeof Intl.Segmenter === 'function'
        ? new Intl.Segmenter(undefined, { granularity: 'grapheme' })
        : null;
  }
  return graphemes ? [...graphemes.segment(text)].map((s) => s.segment) : [...text];
}

/**
 * A unit's name over its badge row (same anchor, so it moves with the chips
 * and keeps the same pixel gap at any zoom): no pill, white glyphs centred
 * and lifted by NAME_LIFT, each drawn twice (a dark copy one pixel down and
 * right first, as the shadow). Split by grapheme, so flags, ZWJ emoji and
 * combining marks are one glyph. Laid out left to right: right-to-left text
 * is not reordered (known limitation). Empty for an empty name.
 */
export function nameLabelQuads(name: string, measure: Measure): BadgeQuad[] {
  const glyphs = splitGraphemes(name);
  const widths = glyphs.map((g) => measure(g, NAME_FONT, true));
  const total = widths.reduce((a, b) => a + b, 0);
  const shadow: BadgeQuad[] = [];
  const face: BadgeQuad[] = [];
  let x = -total / 2;
  glyphs.forEach((glyph, i) => {
    const w = widths[i];
    const q = {
      kind: 'glyph' as const,
      x: x + w / 2,
      y: NAME_LIFT,
      w,
      h: NAME_FONT * LINE,
      glyph,
      font: NAME_FONT,
      bold: true,
      bob: false,
    };
    shadow.push({ ...q, x: q.x + 1, y: q.y - 1, tint: NAME_SHADOW });
    face.push({ ...q, tint: BADGE_COLORS.fg });
    x += w;
  });
  return [...shadow, ...face];
}
